import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownToLine, ArrowUpFromLine, LineChart, Building2, Loader2, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { isVerified } from "@/lib/account";
import { useAuth } from "@/context/AuthContext";
import AccountStatusBanner from "@/components/dashboard/AccountStatusBanner";
import SummaryCards from "@/components/dashboard/SummaryCards";
import AllocationChart from "@/components/dashboard/AllocationChart";
import HoldingsTable from "@/components/dashboard/HoldingsTable";
import TransactionsTable from "@/components/dashboard/TransactionsTable";
import FundsDialog from "@/components/invest/FundsDialog";
import TestimonialDialog from "@/components/dashboard/TestimonialDialog";

export default function ClientDashboard() {
  const { user, checkAuth } = useAuth();
  const [p, setP] = useState(null);
  const [txs, setTxs] = useState([]);
  const [funds, setFunds] = useState(null);
  const [sharing, setSharing] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/portfolio"), api.get("/transactions")]);
    setP(a.data);
    setTxs(b.data);
  }, []);
  useEffect(() => { load().catch(() => {}); checkAuth(); }, [load, checkAuth]);

  if (!p) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  const verified = isVerified(user);

  return (
    <div className="container-x space-y-8 py-12" data-testid="client-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">Client portfolio</p>
          <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl" data-testid="client-greeting">Hello, {user.name.split(" ")[0]}</h1>
        </div>
        <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:w-auto sm:flex-wrap">
          <button onClick={() => setFunds("deposit")} disabled={!verified} className="btn-primary" data-testid="client-deposit-button"><ArrowDownToLine className="h-4 w-4" /> Deposit</button>
          <button onClick={() => setFunds("withdraw")} disabled={!verified} className="btn-ghost" data-testid="client-withdraw-button"><ArrowUpFromLine className="h-4 w-4" /> Withdraw</button>
          <Link to="/markets" className="btn-ghost" data-testid="client-browse-stocks-link"><LineChart className="h-4 w-4" /> Stocks</Link>
          <Link to="/properties" className="btn-ghost" data-testid="client-browse-properties-link"><Building2 className="h-4 w-4" /> Properties</Link>
          <button onClick={() => setSharing(true)} className="btn-ghost col-span-2 sm:col-span-1" data-testid="client-share-testimonial-button"><MessageSquare className="h-4 w-4" /> Share feedback</button>
        </div>
      </div>

      <AccountStatusBanner user={user} />

      {p.pending_deposits > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-6 py-4 text-sm text-amber-300" data-testid="client-pending-deposit-alert">
          You have deposit requests awaiting approval. Your balance updates once they're approved.
        </div>
      )}

      <SummaryCards p={p} />

      <div className="grid gap-6 lg:grid-cols-4">
        <div className="min-w-0 lg:col-span-3"><HoldingsTable p={p} onDone={load} /></div>
        <div className="min-w-0"><AllocationChart p={p} /></div>
      </div>

      <div className="panel p-5 sm:p-8">
        <p className="eyebrow mb-2 sm:mb-4">Transactions & requests</p>
        <TransactionsTable items={txs} />
      </div>

      <FundsDialog open={!!funds} onOpenChange={(o) => !o && setFunds(null)} mode={funds || "deposit"}
        available={p.cash_balance - p.pending_withdrawals} onDone={load} />
      <TestimonialDialog open={sharing} onOpenChange={setSharing} />
    </div>
  );
}
